import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2, Save, Trash2, Plus, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { AppHeader } from "@/components/AppHeader";
import { SupportButton } from "@/components/SupportButton";
import { inputClass, labelClass, btnPrimary, btnGhost, iconBtnClass } from "@/components/Brand";
import { TemplateManager } from "@/components/TemplateManager";
import { SorterDownloads } from "@/components/SorterDownloads";
import { rowKey, withRowKeys } from "@/lib/rowKey";
import { logError } from "@/lib/log";

export default function Settings() {
  const { checkAuth } = useAuth();
  const { t } = useI18n();
  const [studio, setStudio] = useState(null);
  const [branding, setBranding] = useState({ name: "", logo_url: "", whatsapp_number: "" });
  const [pricing, setPricing] = useState({ extra_edit_price: 5000, frame_price: 50000, sizes: [] });
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "admin" });
  const [busy, setBusy] = useState("");
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    const res = await api.get("/studio/templates");
    setTemplates(res.data);
  }, []);

  const load = useCallback(async () => {
    const res = await api.get("/studio");
    setStudio(res.data);
    setBranding({ name: res.data.name || "", logo_url: res.data.logo_url || "", whatsapp_number: res.data.whatsapp_number || "" });
    setPricing({
      extra_edit_price: res.data.pricing?.extra_edit_price ?? 5000,
      frame_price: res.data.pricing?.frame_price ?? 50000,
      sizes: withRowKeys(Object.entries(res.data.pricing?.print_prices || {}).map(([size, price]) => ({ size, price }))),
    });
    try {
      const s = await api.get("/studio/staff");
      setStaff(s.data);
    } catch (err) {
      logError("Could not load staff", err);
    }
    await loadTemplates().catch((err) => logError("Could not load templates", err));
  }, [loadTemplates]);

  useEffect(() => {
    load();
  }, [load]);

  const saveBranding = async (e) => {
    e.preventDefault();
    setBusy("brand");
    try {
      await api.put("/studio/branding", {
        name: branding.name,
        logo_url: branding.logo_url || null,
        whatsapp_number: branding.whatsapp_number || "",
      });
      await checkAuth();
      toast.success(t("saved_ok"));
    } catch (err) {
      toast.error(apiError(err, "Could not save"));
    } finally {
      setBusy("");
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/studio/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setBranding((b) => ({ ...b, logo_url: res.data.logo_url }));
      await checkAuth();
      toast.success(t("saved_ok"));
    } catch (err) {
      toast.error(apiError(err, "Could not upload logo"));
    } finally {
      setBusy("");
    }
  };

  const savePricing = async (e) => {    e.preventDefault();
    setBusy("price");
    try {
      const print_prices = {};
      pricing.sizes.filter((s) => s.size).forEach((s) => (print_prices[s.size] = Number(s.price) || 0));
      await api.put("/studio/pricing", {
        extra_edit_price: Number(pricing.extra_edit_price),
        frame_price: Number(pricing.frame_price),
        print_prices,
        default_print_sizes: Object.keys(print_prices),
      });
      toast.success(t("saved_ok"));
    } catch (err) {
      toast.error(apiError(err, "Could not save"));
    } finally {
      setBusy("");
    }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    setBusy("staff");
    try {
      await api.post("/studio/staff", newStaff);
      setNewStaff({ name: "", email: "", password: "", role: "admin" });
      await load();
      toast.success(t("add_staff"));
    } catch (err) {
      toast.error(apiError(err, "Could not add staff"));
    } finally {
      setBusy("");
    }
  };

  if (!studio)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#707070]">
        <Loader2 className="animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="settings-page">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-5 lg:px-12 py-12 lg:py-16 space-y-10">
        <div>
          <p className="eyebrow mb-3">{t("settings")}</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium">{studio.name}</h1>
        </div>

        <form onSubmit={saveBranding} className="border border-[#2A2A2A] bg-[#0F0F0F] p-7 space-y-6" data-testid="branding-panel">
          <p className="eyebrow">{t("branding")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>{t("studio_name")}</label>
              <input data-testid="branding-name-input" className={inputClass} value={branding.name}
                onChange={(e) => setBranding({ ...branding, name: e.target.value })} required />
            </div>
            <div>
              <label className={labelClass}>{t("logo_url")}</label>
              <div className="flex items-center gap-3 mt-1">
                <label
                  data-testid="logo-upload-label"
                  className="cursor-pointer inline-flex items-center gap-2 border border-[#2A2A2A] px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase hover:border-[#D4AF37] transition-colors"
                >
                  {busy === "logo" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {busy === "logo" ? t("uploading") : t("upload_logo")}
                  <input data-testid="logo-file-input" type="file"
                    accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
              </div>
              <input data-testid="branding-logo-input" className={`${inputClass} mt-3 truncate`} value={branding.logo_url}
                onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                placeholder={t("or_paste_url")} />
              <p className="mt-2 text-[11px] text-[#707070] leading-relaxed">{t("logo_hint")}</p>
              {branding.logo_url && (
                <span className="mt-4 inline-flex items-center bg-[#F3F3F1] border border-[#2A2A2A] px-3 py-2">
                  <img src={branding.logo_url} alt="logo" className="h-9 w-auto object-contain"
                    data-testid="branding-logo-preview" />
                </span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("studio_whatsapp")}</label>
              <input data-testid="branding-whatsapp-input" className={inputClass} value={branding.whatsapp_number}
                onChange={(e) => setBranding({ ...branding, whatsapp_number: e.target.value })} placeholder="08123456789" />
            </div>
          </div>
          <button data-testid="save-branding-btn" disabled={busy === "brand"} className={btnPrimary}>
            {busy === "brand" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("save_changes")}
          </button>
        </form>

        <form onSubmit={savePricing} className="border border-[#2A2A2A] bg-[#0F0F0F] p-7 space-y-6" data-testid="pricing-panel">
          <p className="eyebrow">{t("pricing_defaults")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>{t("extra_edit_price")}</label>
              <input data-testid="extra-edit-price-input" type="number" className={inputClass} value={pricing.extra_edit_price}
                onChange={(e) => setPricing({ ...pricing, extra_edit_price: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("frame_price")}</label>
              <input data-testid="frame-price-input" type="number" className={inputClass} value={pricing.frame_price}
                onChange={(e) => setPricing({ ...pricing, frame_price: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>{t("price_per_size")}</label>
            <div className="space-y-3 mt-3">
              {pricing.sizes.map((s, idx) => (
                <div key={s._k} className="grid grid-cols-[1fr_8rem_1.5rem] gap-3 items-center">
                  <input data-testid={`price-size-${idx}`} className={inputClass} value={s.size}
                    onChange={(e) => setPricing((p) => ({
                      ...p, sizes: p.sizes.map((x, n) => (n === idx ? { ...x, size: e.target.value } : x)),
                    }))} placeholder="8RP(SC)" />
                  <input data-testid={`price-amount-${idx}`} type="number" className={inputClass} value={s.price}
                    onChange={(e) => setPricing((p) => ({
                      ...p, sizes: p.sizes.map((x, n) => (n === idx ? { ...x, price: e.target.value } : x)),
                    }))} />
                  <button type="button" data-testid={`remove-price-row-${idx}`}
                    onClick={() => setPricing((p) => ({ ...p, sizes: p.sizes.filter((_, n) => n !== idx) }))}
                    aria-label={t("remove")}
                    className={`${iconBtnClass} text-[#5A5A5A] hover:text-[#8B0000]`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <button type="button" data-testid="add-price-row-btn"
              onClick={() => setPricing((p) => ({ ...p, sizes: [...p.sizes, { size: "", price: 0, _k: rowKey() }] }))}
              className="mt-4 inline-flex items-center min-h-[44px] text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] hover:underline">
              <Plus size={12} className="inline" /> {t("add_size")}
            </button>
          </div>
          <button data-testid="save-pricing-btn" disabled={busy === "price"} className={btnPrimary}>
            {busy === "price" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("save_changes")}
          </button>
        </form>

        <SorterDownloads path="/studio/sorter/phase2" filePrefix="seleksa_phase2"
          titleKey="sorter_phase2_title" bodyKey="sorter_phase2_body" testIdPrefix="sorter-phase2" />

        <TemplateManager templates={templates} onChange={loadTemplates} />

        <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7 space-y-6" data-testid="staff-panel">          <p className="eyebrow">{t("staff")}</p>
          <div className="space-y-3">
            {staff.map((s) => (
              <div key={s.user_id} className="flex items-center justify-between border-b border-[#1E1E1E] pb-3">
                <div>
                  <p className="text-sm">{s.name}</p>
                  <p className="text-xs text-[#707070]">{s.email}</p>
                </div>
                <span className="flex items-center gap-4">
                  <span className="font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#D4AF37]">{s.tenant_role}</span>
                  <button data-testid={`remove-staff-${s.user_id}`}
                    onClick={async () => {
                      try {
                        await api.delete(`/studio/staff/${s.user_id}`);
                        await load();
                        toast.success(t("remove"));
                      } catch (err) {
                        toast.error(apiError(err, "Could not remove"));
                      }
                    }}
                    className={`${iconBtnClass} text-[#5A5A5A] hover:text-[#8B0000]`}
                    aria-label={t("remove")}><Trash2 size={14} /></button>
                </span>
              </div>
            ))}
          </div>
          <form onSubmit={addStaff} className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <input data-testid="staff-name-input" className={inputClass} placeholder={t("your_name")} value={newStaff.name}
              onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} required />
            <input data-testid="staff-email-input" type="email" className={inputClass} placeholder={t("email")} value={newStaff.email}
              onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} required />
            <input data-testid="staff-password-input" type="password" className={inputClass} placeholder={t("password")} value={newStaff.password}
              onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} required />
            <select data-testid="staff-role-select" value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
              className="bg-[#050505] border border-[#2A2A2A] px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none">
              <option value="admin">admin</option>
              <option value="owner">owner</option>
            </select>
            <button data-testid="add-staff-btn" disabled={busy === "staff"} className={`${btnGhost} sm:col-span-4`}>
              {t("add_staff")}
            </button>
          </form>
        </div>
      </main>
      <SupportButton />
    </div>
  );
}
